import { runAllLimitingConcurrency } from 'base/concurrency';
import { exists, unique } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import {
  IconAddComponent,
  IconCopyAi,
  IconShuffleAi,
  IconSitemapAi,
  IconSitemapPageAi,
} from 'base/ui/icons';
import { ToastQueue } from 'base/ui/toast';
import { syncSlotOverrides } from 'blocks/component_renderer/slots_syncing';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { LLMProxyService } from 'blocks/llm_proxy_service';
import { ChangeBuffer, UndoManager } from 'blocks/woot_woot/undo';
import { observable } from 'mobx';
import { EditorMode } from '../editor_mode';
import {
  ElementInstanceState,
  Page,
  Section,
  SectionRef,
  Space,
} from '../model';
import {
  elementTreeDfs,
  pageAndAllSubpages,
  unrefPresentSections,
  unrefSection,
} from '../model_helpers';
import { SelectionStore } from '../selection/store';
import { ProjectSession } from '../session/session';
import { ConceptAPI } from '../styleguide/concept_store';
import { globalElementViewOptions } from '../wireframe/element_view_options';
import { ApprovedCategories } from './approved_categories';
import { ComponentContentAi } from './component_content';
import { PageComponentsAi } from './page_components';
import { PageSchemesAi } from './page_schemes';
import {
  clearHomepage,
  isUserWrittenName,
  PageSectionsAi,
} from './page_sections';
import { SectionToComponentCategoryAi } from './section_to_component_category';
import {
  AiTaskLog,
  LLMErrorFormatter,
  TaskContext,
  TaskMeta,
} from './task_log';
import { TextRewritingAi } from './text_rewriting';
import { installAiToasts, showAllPagesCelebrationToast } from './toasts';
import { getPageContextError } from './validation';

export function createAssistant(
  selection: SelectionStore,
  session: ProjectSession,
  undo: UndoManager,
  llm: LLMProxyService,
  library: LibraryCache,
  flags: FeatureFlagsStore,
  toastQueue: ToastQueue,
) {
  const approvedCategories = ApprovedCategories.createLoader(library);
  const sectionToComponentCategoryAi = new SectionToComponentCategoryAi(
    llm,
    approvedCategories,
    selection.project,
  );
  const componentContentAi = new ComponentContentAi(selection, library, llm);
  const pageComponentsAi = new PageComponentsAi(
    sectionToComponentCategoryAi,
    library,
    selection.project,
    selection.factory,
  );
  const pageSectionsAi = new PageSectionsAi(
    llm,
    approvedCategories,
    selection.project,
    undo,
    selection.factory,
  );
  const pageSchemesAi = new PageSchemesAi(llm, selection.project);
  const errorFormatter = new LLMErrorFormatter(session);
  const taskLog = new AiTaskLog(undo, errorFormatter);
  const { feedbackLayer, dispose } = installAiToasts(
    llm,
    taskLog,
    toastQueue,
    session.showAuthWall ? 'anonymous' : 'logged-in',
  );
  let hasCelebrated = false;
  const assistant = new Assistant(
    taskLog,
    (...args) => llm.postFeedback(...args),
    undo,
    selection,
    session,
    () => {
      if (hasCelebrated) return;
      hasCelebrated = true;
      showAllPagesCelebrationToast(toastQueue);
    },
    approvedCategories,
    pageSectionsAi,
    pageComponentsAi,
    sectionToComponentCategoryAi,
    componentContentAi,
    new TextRewritingAi(selection.project, llm, errorFormatter),
    pageSchemesAi,
    library,
    flags,
  );
  return [assistant, feedbackLayer] as const;
}

type GenerateSpaceOpts = { pageCount: number } & GeneratePagesOpts;
type GeneratePagesOpts = { chooseComponents: boolean } & ChooseComponentsOpts &
  (
    | { applyDesignSchemes?: never }
    | { applyDesignSchemes: true; concept: ConceptAPI }
  );
type ChooseComponentsOpts = {
  replaceExistingComponents?: boolean;
  generateCopy: boolean;
};
type GenerateCopyOpts = { disableNotifications?: boolean };

/** Binds the AI services to the AiTaskLog */
export class Assistant {
  constructor(
    readonly taskLog: AiTaskLog,
    readonly postFeedback: LLMProxyService['postFeedback'],
    private readonly undo: UndoManager,
    private readonly selection: Pick<SelectionStore, 'project' | 'factory'>,
    private readonly session: ProjectSession,
    private readonly showAllPagesCelebrationToast: () => void,
    private readonly loadApprovedCategories: () => Promise<ApprovedCategories>,
    private readonly pageSections: PageSectionsAi,
    private readonly pageComponents: PageComponentsAi,
    private readonly sectionToComponentCategoryAi: SectionToComponentCategoryAi,
    private readonly componentContent: ComponentContentAi,
    readonly textRewriting: TextRewritingAi,
    private readonly pageSchemes: PageSchemesAi,
    private readonly library: LibraryCache,
    private readonly flags: FeatureFlagsStore,
  ) {}

  /** Generates a whole sitemap including generating sections in the newly created pages. */
  async generateSpace(space: Space | 'home', opts: GenerateSpaceOpts) {
    const { project } = this.selection;
    const undoEntry = this.undo.createBuffer();
    const approvedCategories = await this.loadApprovedCategories();
    const home = undoEntry.run(() =>
      clearHomepage(project, space, approvedCategories.homeTranslations),
    );

    const meta: TaskMeta & { useExistingUndoEntry: ChangeBuffer } = {
      icon: IconSitemapAi,
      titleWhenRunning: opts.chooseComponents
        ? 'Generating wireframes...'
        : 'Generating sitemap...',
      thirdPersonTooltip: opts.chooseComponents
        ? 'AI is generating wireframes'
        : 'AI is generating a sitemap',
      errorTooltip: 'Error generating sitemap',
      affects: [home],
      useExistingUndoEntry: undoEntry,
    };
    this.taskLog.startTask(meta, async (ctx) => {
      ctx.buffer.run(() => {
        home.sections.clear();
        home.subPages.clear();
      });

      const removeLoader =
        this.session.decorations.setPageSectionsPending(home);
      try {
        // Generate sitemap and home page
        await this.pageSections.generateProject(
          project,
          space,
          home,
          opts.pageCount,
          ctx,
        );
        // Generate the other pages
        const allPagesMinusHome =
          space === 'home'
            ? pageAndAllSubpages(project.homePage).filter(
                (page) => page !== project.homePage,
              )
            : space.subPages.flatMap(pageAndAllSubpages);
        await this.generatePages(allPagesMinusHome, {
          ...opts,
          replaceExistingComponents: false,
        });
      } finally {
        removeLoader();
      }
      if (opts.chooseComponents) {
        await this.doChooseComponents(home, home.sections, opts, ctx);
      }
    });
  }

  /** Generates sections for a page, and chooses components (if in wireframe mode) */
  generatePages(pages: readonly Page[], opts: GeneratePagesOpts) {
    return new Promise((resolve, reject) => {
      const pagesCompleteBox = observable.box(0);
      const meta: TaskMeta = {
        icon: IconSitemapPageAi,
        titleWhenRunning:
          pages.length === 1
            ? 'Generating page...'
            : () => {
                const complete = pagesCompleteBox.get();
                return `Generating ${complete + 1} of ${pages.length} pages...`;
              },
        thirdPersonTooltip: 'AI is generating a page',
        errorTooltip: 'Error generating page',
        affects: pages,
      };

      this.taskLog.startTask(meta, async (ctx) => {
        ctx.buffer.run(() => pages.forEach((page) => page.sections.clear()));
        const removeLoaders = pages.map((page) =>
          this.session.decorations.setPageSectionsPending(page),
        );

        // lol why did I write it to process batches
        // The output is more verbose when we use batch size 1.  So we only use batch size 1
        const batches = evenBatches(pages, 1);
        let chooseComponentPromises: Promise<void>[] = [];
        try {
          await runAllLimitingConcurrency(
            batches.map((batch) => async () => {
              await this.pageSections.generatePageSections(
                batch,
                ctx,
                (page) => {
                  if (opts.chooseComponents) {
                    chooseComponentPromises.push(
                      this.doChooseComponents(
                        page,
                        [...page.sections],
                        opts,
                        ctx,
                      ),
                    );
                  }
                  removeLoaders[pages.indexOf(page)]?.();
                  pagesCompleteBox.set(pagesCompleteBox.get() + 1);
                },
              );
            }),
          );

          await Promise.all(chooseComponentPromises);

          if (opts.applyDesignSchemes) {
            await Promise.all(
              pages.map((page) =>
                this.pageSchemes.applyDesignSchemesToPage({
                  page,
                  concept: opts.concept,
                  ctx,
                }),
              ),
            );
          }
        } catch (e) {
          reject(e);
        } finally {
          removeLoaders.forEach((r) => r?.());
        }

        if (pages.length > 1) this.showAllPagesCelebrationToast();

        resolve(undefined);
      });
    });
  }

  applyDesignSchemesToPages(pages: readonly Page[], concept: ConceptAPI) {
    const meta: TaskMeta = {
      icon: IconShuffleAi,
      titleWhenRunning: 'Applying schemes...',
      thirdPersonTooltip: 'AI is applying schemes',
      errorTooltip: 'Error applying schemes',
      affects: pages,
    };
    this.taskLog.startTask(meta, async (ctx) => {
      await Promise.all(
        pages.map((page) =>
          this.pageSchemes.applyDesignSchemesToPage({
            page,
            concept,
            ctx,
          }),
        ),
      );
    });
  }

  isRewriting(section: Section): boolean {
    const el = section?.element.current;
    return el != null && this.componentContent.pending.has(el);
  }

  generateCopy(
    /** Map of section to old root element (if any) to re-use copy from */
    srefs: ReadonlyMap<SectionRef, ElementInstanceState | undefined>,
    opts?: GenerateCopyOpts,
  ) {
    const sections = [...srefs.entries()]
      .map(([sref, oldInstance]) => {
        const { page } = this.selection.factory.forSection(sref);
        const section = unrefSection(sref, this.selection.project);
        if (!section) return;
        return { section, page, oldInstance };
      })
      .filter(unique())
      .filter(exists);
    if (sections.length === 0) throw new Error('No sections');
    const page = sections[0].page;
    if (!sections.every((v) => v.page === page)) {
      throw new Error(
        'Generate copy called with sections from different pages',
      );
    }
    if (!this.session.canViewWireframe(page)) {
      throw new Error(
        'Why generate copy for a page you cannot view in wireframe mode?',
      );
    }

    const meta: TaskMeta = {
      icon: IconCopyAi,
      titleWhenRunning: 'Generating copy...',
      thirdPersonTooltip: 'AI is generating copy',
      errorTooltip: 'Error generating copy',
      affects: sections.length === 1 ? sections.map((v) => v.section) : [page],
      onlyNotifyIn: opts?.disableNotifications ? 'never' : EditorMode.Wireframe,
    };

    this.taskLog.startTask(meta, async (ctx) =>
      Promise.all(
        sections.map(async ({ oldInstance, section, page }) => {
          section.isComponentOutOfSync = false;
          const instance = section.element.current;
          if (!instance) return;

          // Ensure the component JSON has been fetched, and that slot overrides
          // have been populated, before we attempt to generate copy. If slot
          // overrides haven't been synced before copy generation, we'll end up
          // incorrectly attaching the text overrides to the root element,
          // rather than the leaf elements in each slot.
          await this.library.getPublicComponentJson(instance.source.slug);

          syncSlotOverrides({
            element: instance,
            library: this.library,
            undo: this.undo,
          });

          // Show where we're writing
          const viewOptions = globalElementViewOptions.get(instance);
          const openMenusBefore = viewOptions.openMenus;
          viewOptions.openMenus = true;
          return this.componentContent
            .generateCopy(instance, oldInstance, section, page, ctx)
            .finally(() => (viewOptions.openMenus = openMenusBefore));
        }),
      ).then(() => undefined),
    );
  }

  isChoosingComponents(page: Page) {
    return this.pageComponents.isPending(page);
  }

  shuffleComponent(page: Page, sectionRef: SectionRef) {
    if (this.isChoosingComponents(page)) throw new Error('Already choosing');
    const section = unrefSection(sectionRef, this.selection.project);
    if (!section) throw new Error('Section not found');
    const hasExisting = section.element.current != null;
    const verb = hasExisting ? 'Shuffling' : 'Adding';
    const meta: TaskMeta = {
      icon: hasExisting ? IconShuffleAi : IconAddComponent,
      titleWhenRunning: `${verb} component...`,
      thirdPersonTooltip: `AI is ${verb.toLowerCase()} a component`,
      errorTooltip: `Error ${verb.toLowerCase()} component`,
      affects: [section],
      onlyNotifyIn: EditorMode.Wireframe,
    };
    this.taskLog.startTask(meta, async (ctx) =>
      this.doChooseComponents(page, [sectionRef], { generateCopy: true }, ctx),
    );
  }

  chooseComponents(page: Page, opts: ChooseComponentsOpts) {
    if (this.isChoosingComponents(page)) throw new Error('Already choosing');
    const sections = opts.replaceExistingComponents
      ? [...page.sections]
      : page.sections.filter((sref) => {
          const section = unrefSection(sref, this.selection.project);
          return section && section.element.current == null;
        });
    if (sections.length === 0) return;
    const meta: TaskMeta = {
      icon: IconShuffleAi,
      titleWhenRunning: `Generating wireframes...`,
      thirdPersonTooltip: 'AI is adding components',
      errorTooltip: 'Error adding components',
      affects: [page],
      onlyNotifyIn: EditorMode.Wireframe,
    };
    this.taskLog.startTask(meta, async (ctx) =>
      this.doChooseComponents(page, sections, opts, ctx),
    );
  }

  private async doChooseComponents(
    page: Page,
    srefs: readonly SectionRef[],
    opts: ChooseComponentsOpts,
    ctx: TaskContext,
  ) {
    if (!this.session.canViewWireframe(page)) {
      return; // don't do invisible work
    }
    const sections = unrefPresentSections(srefs, this.selection.project);
    await this.pageComponents.chooseComponents(page, sections, ctx);
    const sel = this.selection.factory.forPage(page);
    if (
      opts.generateCopy &&
      getPageContextError(
        page.state,
        sel.space,
        this.selection.project.state,
      ) == null &&
      isUserWrittenName(page.name)
    ) {
      const geneateCopyFor = this.pageSectionsToGenerateCopyFor(srefs);
      geneateCopyFor.length &&
        this.generateCopy(
          new Map(geneateCopyFor.map((sref) => [sref, undefined])),
          { disableNotifications: true },
        );
    }
  }

  /**
   * When generating copy for a whole page, we ignore:
   *
   * - sections without components
   * - sections without user-written names
   * - globals that already have copy
   **/
  pageSectionsToGenerateCopyFor(srefs: readonly SectionRef[]) {
    return srefs.filter((sref) => {
      const section = unrefSection(sref, this.selection.project);
      if (!section) return false;
      if (!isUserWrittenName(section.name)) return false;
      if (this.isRewriting(section)) return false;
      const rootEl = section.element.current;
      if (!rootEl) return false;
      if (sref.type === 'reference') {
        for (const el of elementTreeDfs(rootEl)) {
          if (el.textOverrides.size > 0) return false;
        }
      }
      return true;
    });
  }

  readonly getBlocksCategoryForSection =
    this.sectionToComponentCategoryAi.getBlocksCategoryForSection;
}

export function evenBatches<T>(
  items: readonly T[],
  maxBatchSize: number,
): T[][] {
  const numBatches = Math.ceil(items.length / maxBatchSize);
  const targetBatchSize = Math.ceil(items.length / numBatches);
  const batches: T[][] = [];
  for (let i = 0; i < numBatches; i++) {
    batches.push(items.slice(i * targetBatchSize, (i + 1) * targetBatchSize));
  }
  return batches;
}
